import { createEffect, createResource, createSignal, For, on, Show, type Component } from 'solid-js';

import { fetchInvoices, recordPayment, setCreditLimit } from '../lib/api.js';
import { moneyOrDash, toMajorUnits } from '../lib/format.js';
import type { CompanyAgingRow, InvoiceRow } from '../lib/types.js';

/** Tone for the invoice status chip — overdue reads critical, settled reads success. */
function statusTone(status: InvoiceRow['status']): string {
  if (status === 'PAID') {
    return 'success';
  }
  if (status === 'OPEN') {
    return 'warning';
  }
  return 'neutral';
}

/**
 * One company's invoices, its credit limit, and payment entry (SPEC FR-2, FR-4).
 *
 * An inline panel directly beneath the aging table, NOT an `s-modal`. That was tried first and
 * withdrawn on 25 Aug 2026: Polaris opens s-modal's internal `<dialog>` with `show()` rather than
 * `showModal()`, so it never enters the browser's top layer — `document.querySelectorAll('dialog:modal')`
 * stays empty — and the panel paints *behind* the cards that follow it in the document. The
 * documented `command="--show"` / `commandfor` invoker did not open it either. Rather than ship an
 * overlay that could not be verified working, the detail stays in the flow where it is guaranteed to
 * render, and `scrollIntoView` supplies the "something happened" feedback a modal would have given.
 */
export const CompanyDetail: Component<{
  company: CompanyAgingRow;
  /** Changes on every row click, so re-clicking the SAME row still re-runs the scroll effect. */
  openToken: number;
  currency: string;
  onClose: () => void;
  onChanged: () => void;
}> = (props) => {
  const [invoices, { refetch: refetchInvoices }] = createResource(
    () => props.company.companyId,
    fetchInvoices,
  );

  let panel: HTMLElement | undefined;

  // Bring the panel into view on every open. Without this, clicking a row near the bottom of a long
  // aging table appears to do nothing: the detail renders below the fold and the merchant is left
  // wondering what their click did.
  //
  // `on(...)` states the dependency explicitly — a bare `props.openToken;` statement to "touch" the
  // signal is a no-op expression the compiler is free to drop, taking the subscription with it.
  // setTimeout, not requestAnimationFrame: rAF does not fire while a tab is hidden, and this app
  // lives in an iframe the merchant can background at any moment.
  createEffect(
    on(
      () => props.openToken,
      () => {
        setTimeout(() => panel?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      },
    ),
  );

  return (
    <s-section ref={panel} heading={`${props.company.name} — detail`}>
      <s-stack direction="block" gap="large">
        <CreditLimit
          company={props.company}
          currency={props.currency}
          onSaved={() => props.onChanged()}
        />

        <s-divider />

        <s-stack direction="block" gap="base">
          <s-heading>Invoices</s-heading>
          {/* `loading` is built into s-table; the hand-rolled s-spinner this replaced left the
              table's own header flashing in and out on every refetch. */}
          <s-table loading={invoices.loading ? 'true' : undefined}>
            <s-table-header-row>
              <s-table-header>Order</s-table-header>
              <s-table-header>Issued</s-table-header>
              <s-table-header>Due</s-table-header>
              <s-table-header>Terms</s-table-header>
              <s-table-header>Open</s-table-header>
              <s-table-header>Status</s-table-header>
            </s-table-header-row>
            <s-table-body>
              <For each={invoices() ?? []}>
                {(invoice) => (
                  <s-table-row>
                    <s-table-cell>{invoice.orderName}</s-table-cell>
                    <s-table-cell>{invoice.issuedAt}</s-table-cell>
                    <s-table-cell>{invoice.dueAt ?? '—'}</s-table-cell>
                    <s-table-cell>{invoice.termsName ?? '—'}</s-table-cell>
                    <s-table-cell>{moneyOrDash(invoice.openCents)}</s-table-cell>
                    <s-table-cell>
                      <s-badge tone={statusTone(invoice.status)}>{invoice.status}</s-badge>
                    </s-table-cell>
                  </s-table-row>
                )}
              </For>
            </s-table-body>
          </s-table>
        </s-stack>

        <s-divider />

        <RecordPayment
          invoices={(invoices() ?? []).filter((invoice) => invoice.status === 'OPEN')}
          currency={props.currency}
          onRecorded={() => {
            void refetchInvoices();
            props.onChanged();
          }}
        />

        <s-stack direction="inline" justifyContent="end">
          <s-button variant="secondary" onClick={() => props.onClose()}>
            Close
          </s-button>
        </s-stack>
      </s-stack>
    </s-section>
  );
};

/**
 * The credit limit (SPEC FR-2) — the control the checkout Function depends on.
 *
 * Uses `s-money-field`, not `s-text-field`: it renders the shop's currency, rejects non-numeric
 * input at the field rather than at the API, and carries an `error` slot for the rejection message.
 */
const CreditLimit: Component<{
  company: CompanyAgingRow;
  currency: string;
  onSaved: () => void;
}> = (props) => {
  // Major units, because that is what the field shows and what the API accepts back.
  const [value, setValue] = createSignal(
    props.company.limitCents === null ? '' : toMajorUnits(props.company.limitCents),
  );
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [message, setMessage] = createSignal<string | null>(null);

  async function save(limit: string | null): Promise<void> {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await setCreditLimit(props.company.companyId, limit);
      setMessage(
        limit === null
          ? 'Limit cleared. Checkout will no longer be blocked for this company.'
          : 'Saved. Checkout enforcement updates within a minute.',
      );
      props.onSaved();
    } catch (caught) {
      setError(`Could not save: ${String(caught)} — email support@duebook.app if this persists.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <s-stack direction="block" gap="base">
      <s-heading>Credit limit</s-heading>
      <s-text tone="subdued">
        An order that would take this company over its limit is blocked at checkout with a message
        you can customise.
      </s-text>
      <s-stack direction="inline" gap="base" alignItems="end">
        <s-money-field
          label="Credit limit"
          // attr: forces the ATTRIBUTE. `currencyCode` already exists as a property on the
          // upgraded element, so Solid assigns the property instead — and the component only
          // reacts to `currencycode`, leaving the field on its "auto" default.
          attr:currencycode={props.currency}
          value={value()}
          min="0"
          error={error() ?? undefined}
          onInput={(event: Event) => setValue((event.target as HTMLInputElement | null)?.value ?? '')}
        />
        <s-button
          loading={busy() ? 'true' : undefined}
          onClick={() => void save(value() === '' ? null : value())}
        >
          Save limit
        </s-button>
        <Show when={props.company.limitCents !== null}>
          <s-button variant="tertiary" loading={busy() ? 'true' : undefined} onClick={() => void save(null)}>
            Clear
          </s-button>
        </Show>
      </s-stack>
      <Show when={message()}>
        <s-banner tone="success" dismissible>
          <s-paragraph>{message()}</s-paragraph>
        </s-banner>
      </Show>
    </s-stack>
  );
};

/**
 * Record a payment that arrived outside Shopify — a wire or a cheque (SPEC FR-4).
 *
 * This had no UI at all until 25 Aug 2026: `recordPayment` sat in lib/api.ts with no caller, so a
 * merchant paid by bank transfer had no way to settle the invoice and the balance stayed wrong
 * forever — the precise failure ("the numbers don't add up") the whole product exists to avoid.
 *
 * The `requestId` is the API's idempotency key, generated per attempt so a double-click cannot
 * post the same payment twice.
 */
const RecordPayment: Component<{
  invoices: InvoiceRow[];
  currency: string;
  onRecorded: () => void;
}> = (props) => {
  const [invoiceId, setInvoiceId] = createSignal('');
  const [amount, setAmount] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [message, setMessage] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const selected = (): string => {
    return invoiceId() === '' ? (props.invoices[0]?.id ?? '') : invoiceId();
  };

  async function submit(): Promise<void> {
    const target = selected();
    if (target === '' || amount() === '') {
      setError('Choose an invoice and enter an amount.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await recordPayment(target, amount(), crypto.randomUUID());
      setMessage(
        result.duplicate
          ? 'That payment was already recorded — nothing was double-counted.'
          : 'Payment recorded. The balance and aging update immediately.',
      );
      setAmount('');
      props.onRecorded();
    } catch (caught) {
      setError(`Could not record it: ${String(caught)} — email support@duebook.app if this persists.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <s-stack direction="block" gap="base">
      <s-heading>Record a payment</s-heading>
      <Show
        when={props.invoices.length > 0}
        fallback={<s-paragraph>No open invoices to pay down.</s-paragraph>}
      >
        <s-text tone="subdued">
          For money that arrived outside Shopify — a bank transfer or a cheque. Payments taken
          through Shopify are picked up automatically.
        </s-text>
        <s-stack direction="inline" gap="base" alignItems="end">
          <s-select
            label="Invoice"
            value={selected()}
            onChange={(event: Event) =>
              setInvoiceId((event.target as HTMLSelectElement | null)?.value ?? '')
            }
          >
            <For each={props.invoices}>
              {(invoice) => (
                <s-option value={invoice.id}>
                  {invoice.orderName} — {moneyOrDash(invoice.openCents)} open
                </s-option>
              )}
            </For>
          </s-select>
          <s-money-field
            label="Amount"
            // attr: forces the ATTRIBUTE. `currencyCode` already exists as a property on the
          // upgraded element, so Solid assigns the property instead — and the component only
          // reacts to `currencycode`, leaving the field on its "auto" default.
          attr:currencycode={props.currency}
            value={amount()}
            min="0"
            error={error() ?? undefined}
            onInput={(event: Event) => setAmount((event.target as HTMLInputElement | null)?.value ?? '')}
          />
          <s-button loading={busy() ? 'true' : undefined} onClick={() => void submit()}>
            Record payment
          </s-button>
        </s-stack>
      </Show>
      <Show when={message()}>
        <s-banner tone="success" dismissible>
          <s-paragraph>{message()}</s-paragraph>
        </s-banner>
      </Show>
    </s-stack>
  );
};
